import ShopManagementActions from "./ShopManagementActions";
import { useNavigate, Link } from "react-router-dom";
import ShopAddressEditor from "./ShopAddressEditor";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaCheck, FaChevronDown, FaExternalLinkAlt, FaSpinner, FaStore } from "react-icons/fa";
import { elakayAutoText as text } from "../pageInside/dataElakay";
import { activeShopOf, selectAccountShop, shopsOfType, useAccountShops } from "./accountShopsStore";
import CreateShopButton from "./CreateShopButton";
import { shopErrorMessage } from "./shopLabels";
import "./shops.css";

const ShopSwitcher = ({ type = "seller", showCreate = true, className = "" }) => {
  const navigate = useNavigate();
  const { data, status } = useAccountShops();
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const [management, setManagement] = useState(null);
  const rootRef = useRef(null);
  const toggleRef = useRef(null);
  const menuRef = useRef(null);

  useLayoutEffect(() => {
    if (!open || !toggleRef.current) return undefined;
    const place = () => {
      const rect = toggleRef.current.getBoundingClientRect();
      const width = Math.min(340, Math.max(0, window.innerWidth - 32));
      setMenuPosition({
        top: rect.bottom + 6,
        left: Math.max(16, Math.min(rect.left, window.innerWidth - width - 16)),
        width,
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open || management) return undefined;
    const onDown = (event) => {
      const inside = rootRef.current?.contains(event.target) || menuRef.current?.contains(event.target);
      if (!inside) setOpen(false);
    };
    const onKey = (event) => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, management]);

  if (!data || !(data.types || []).includes(type)) {
    return status === "loading"
      ? <span className="msh-switcher-loading" role="status"><FaSpinner className="msh-spin" aria-hidden="true" /></span>
      : null;
  }

  const shops = data.shops || [];
  const workspaceShops = shopsOfType(data, type);
  const active = activeShopOf(data, type) || workspaceShops[0] || null;

  const choose = async (shop) => {
    if (String(shop._id) === String(active?._id)) { setOpen(false); return; }
    setBusyId(shop._id);
    setError(null);
    try {
      await selectAccountShop(shop.type, shop._id);
      if (shop.type !== type) navigate(shop.type === "provider" ? "/provider/my-shop" : "/myshop?tab=products&view=admin");
      setOpen(false);
    } catch (err) {
      setError(shopErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const openManagement = (shop, mode) => {
    setManagement({ shop, mode });
    setOpen(false);
  };

  return (
    <div className={`msh-switcher ${className}`} ref={rootRef} data-testid={`shop-switcher-${type}`}>
      <button
        ref={toggleRef}
        type="button"
        className="msh-switcher-toggle"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        title={text("h_msh_my_shops", "My Shops")}
      >
        <FaStore aria-hidden="true" />
        <span className="msh-switcher-name">{active?.name || text("h_msh_my_shops", "My Shops")}</span>
        <FaChevronDown aria-hidden="true" className="msh-switcher-chevron" />
      </button>
      <span className="msh-quota-pill" aria-label={text("h_msh_quota_aria", "{value0} of {value1} shops used", { value0: data.quota?.count ?? 0, value1: data.quota?.max ?? 0 })}>
        {text("h_msh_quota", "{value0} / {value1} Shops", { value0: data.quota?.count ?? 0, value1: data.quota?.max ?? 0 })}
      </span>
      {showCreate && <CreateShopButton type={type} compact />}
      <ShopAddressEditor shop={active} />
      {open && createPortal(
        <div
          ref={menuRef}
          className="msh-menu"
          role="listbox"
          aria-label={text("h_msh_my_shops", "My Shops")}
          style={menuPosition ? { top: menuPosition.top, left: menuPosition.left, width: menuPosition.width } : { visibility: "hidden" }}
        >
          <div className="msh-menu-title">{text("h_msh_my_shops", "My Shops")}</div>
          {shops.map((shop) => {
            const isActive = shop.type === type && String(shop._id) === String(active?._id);
            return (
              <React.Fragment key={shop._id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  className={`msh-option${isActive ? " is-active" : ""}`}
                  onClick={() => choose(shop)}
                  disabled={Boolean(busyId)}
                >
                  <span className="msh-option-main">
                    <strong>{shop.name}</strong>
                    <span className="msh-tag">{text(shop.type === "provider" ? "h_msh_type_provider" : "h_msh_type_seller", shop.type === "provider" ? "Provider" : "Seller")}</span>
                  </span>
                  {shop.location && <span className="msh-option-sub">{shop.location}</span>}
                  {busyId === shop._id ? <FaSpinner className="msh-spin" aria-hidden="true" /> : isActive && <FaCheck aria-hidden="true" className="msh-option-check" />}
                </button>
                <ShopManagementActions shop={shop} shops={shops} onRequestOpen={(mode) => openManagement(shop, mode)} actionsOnly />
              </React.Fragment>
            );
          })}
          {active?.publicPath && (
            <Link className="msh-menu-link" to={active.publicPath} onClick={() => setOpen(false)}>
              <FaExternalLinkAlt aria-hidden="true" /> {text("h_msh_view_shop", "View this shop as a visitor")}
            </Link>
          )}
          {error && <div className="msh-alert msh-alert-error" role="alert">{error}</div>}
        </div>,
        document.body
      )}
      {management && (
        <ShopManagementActions
          key={`${management.shop._id}-${management.mode}`}
          shop={management.shop}
          shops={shops}
          initialMode={management.mode}
          modalOnly
          onClose={() => setManagement(null)}
        />
      )}
    </div>
  );
};
export default ShopSwitcher;
